import { useEffect, useRef, useState } from 'react';
import { GameProps } from './types';
import { TouchControls } from './TouchControls';

type AsteroidsPhase = 'intro' | 'playing' | 'gameover';

interface Ship {
  x: number;
  y: number;
  angle: number;
  vx: number;
  vy: number;
}

interface Asteroid {
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  vertices: { angle: number; dist: number }[];
}

interface Bullet {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
}

interface Star {
  x: number;
  y: number;
  size: number;
  brightness: number;
  twinkleSpeed: number;
  twinkleOffset: number;
  layer: number; // 0 = far, 1 = mid, 2 = close
}

const TWO_PI = Math.PI * 2;
const SHIP_SIZE = 14;
const BULLET_SPEED = 400;
const BULLET_LIFE = 1.2;
const ROTATION_SPEED = 4.5;
const THRUST = 280;
const FRICTION = 0.992;
const MAX_SPEED = 320;

const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v));

const createAsteroid = (x: number, y: number, radius: number): Asteroid => {
  const vertices: { angle: number; dist: number }[] = [];
  const numVerts = 8 + Math.floor(Math.random() * 5);
  for (let i = 0; i < numVerts; i++) {
    vertices.push({
      angle: (i / numVerts) * TWO_PI,
      dist: radius * (0.7 + Math.random() * 0.5),
    });
  }
  const speed = 30 + Math.random() * 50;
  const angle = Math.random() * TWO_PI;
  return {
    x,
    y,
    vx: Math.cos(angle) * speed,
    vy: Math.sin(angle) * speed,
    radius,
    vertices,
  };
};

const wrapPosition = (val: number, max: number): number => {
  if (val < 0) return val + max;
  if (val > max) return val - max;
  return val;
};

const createStars = (width: number, height: number, count: number): Star[] => {
  const stars: Star[] = [];
  for (let i = 0; i < count; i++) {
    const layer = Math.floor(Math.random() * 3);
    stars.push({
      x: Math.random() * width,
      y: Math.random() * height,
      size: layer === 0 ? 1 : layer === 1 ? 1.5 : 2,
      brightness: 0.3 + Math.random() * 0.7,
      twinkleSpeed: 1 + Math.random() * 3,
      twinkleOffset: Math.random() * TWO_PI,
      layer,
    });
  }
  return stars;
};

export function AsteroidsGame({ fgColor, bgColor, softColor, onExit }: GameProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number | null>(null);
  const [isTouchDevice, setIsTouchDevice] = useState(false);

  const keysRef = useRef({ left: false, right: false, up: false, space: false });

  const stateRef = useRef({
    phase: 'intro' as AsteroidsPhase,
    width: 0,
    height: 0,
    deviceRatio: 1,
    ship: { x: 0, y: 0, angle: -Math.PI / 2, vx: 0, vy: 0 } as Ship,
    asteroids: [] as Asteroid[],
    bullets: [] as Bullet[],
    stars: [] as Star[],
    score: 0,
    lastTime: 0,
    shootCooldown: 0,
    level: 1,
    time: 0,
  });

  const spawnAsteroids = (count: number) => {
    const state = stateRef.current;
    const newAsteroids: Asteroid[] = [];
    for (let i = 0; i < count; i++) {
      let x: number, y: number;
      // Spawn away from ship
      do {
        x = Math.random() * state.width;
        y = Math.random() * state.height;
      } while (
        Math.hypot(x - state.ship.x, y - state.ship.y) < 120
      );
      newAsteroids.push(createAsteroid(x, y, 35 + Math.random() * 15));
    }
    state.asteroids.push(...newAsteroids);
  };

  const resetRound = () => {
    const state = stateRef.current;
    state.phase = 'intro';
    state.ship = {
      x: state.width / 2,
      y: state.height / 2,
      angle: -Math.PI / 2,
      vx: 0,
      vy: 0,
    };
    state.asteroids = [];
    state.bullets = [];
    state.score = 0;
    state.lastTime = 0;
    state.shootCooldown = 0;
    state.level = 1;
    spawnAsteroids(4);
  };

  const resizeCanvas = () => {
    const container = containerRef.current;
    const canvas = canvasRef.current;
    if (!container || !canvas) return;

    const bounds = container.getBoundingClientRect();
    const width = Math.max(280, Math.floor(bounds.width));
    const height = Math.max(200, Math.floor(bounds.height));
    const deviceRatio = window.devicePixelRatio || 1;

    canvas.width = Math.floor(width * deviceRatio);
    canvas.height = Math.floor(height * deviceRatio);
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;

    const state = stateRef.current;
    state.width = width;
    state.height = height;
    state.deviceRatio = deviceRatio;
    state.stars = createStars(width, height, 80);

    resetRound();
  };

  const shoot = () => {
    const state = stateRef.current;
    if (state.shootCooldown > 0) return;

    const ship = state.ship;
    state.bullets.push({
      x: ship.x + Math.cos(ship.angle) * SHIP_SIZE,
      y: ship.y + Math.sin(ship.angle) * SHIP_SIZE,
      vx: Math.cos(ship.angle) * BULLET_SPEED + ship.vx * 0.3,
      vy: Math.sin(ship.angle) * BULLET_SPEED + ship.vy * 0.3,
      life: BULLET_LIFE,
    });
    state.shootCooldown = 0.15;
  };

  const update = (timestamp: number) => {
    const state = stateRef.current;
    if (!state.lastTime) state.lastTime = timestamp;

    const delta = clamp((timestamp - state.lastTime) / 1000, 0, 0.05);
    state.lastTime = timestamp;
    state.time += delta;

    if (state.phase !== 'playing') return;

    const keys = keysRef.current;
    const ship = state.ship;

    // Rotation
    if (keys.left) ship.angle -= ROTATION_SPEED * delta;
    if (keys.right) ship.angle += ROTATION_SPEED * delta;

    // Thrust
    if (keys.up) {
      ship.vx += Math.cos(ship.angle) * THRUST * delta;
      ship.vy += Math.sin(ship.angle) * THRUST * delta;
    }

    // Friction & speed limit
    ship.vx *= FRICTION;
    ship.vy *= FRICTION;
    const speed = Math.hypot(ship.vx, ship.vy);
    if (speed > MAX_SPEED) {
      ship.vx = (ship.vx / speed) * MAX_SPEED;
      ship.vy = (ship.vy / speed) * MAX_SPEED;
    }

    // Move ship
    ship.x = wrapPosition(ship.x + ship.vx * delta, state.width);
    ship.y = wrapPosition(ship.y + ship.vy * delta, state.height);

    // Shoot
    if (keys.space) shoot();
    state.shootCooldown = Math.max(0, state.shootCooldown - delta);

    // Update bullets
    state.bullets = state.bullets
      .map((b) => ({
        ...b,
        x: wrapPosition(b.x + b.vx * delta, state.width),
        y: wrapPosition(b.y + b.vy * delta, state.height),
        life: b.life - delta,
      }))
      .filter((b) => b.life > 0);

    // Update asteroids
    state.asteroids = state.asteroids.map((a) => ({
      ...a,
      x: wrapPosition(a.x + a.vx * delta, state.width),
      y: wrapPosition(a.y + a.vy * delta, state.height),
    }));

    // Bullet-asteroid collision
    const newAsteroids: Asteroid[] = [];
    state.asteroids = state.asteroids.filter((asteroid) => {
      const hitBullet = state.bullets.find(
        (b) => Math.hypot(b.x - asteroid.x, b.y - asteroid.y) < asteroid.radius
      );
      if (hitBullet) {
        state.bullets = state.bullets.filter((b) => b !== hitBullet);
        state.score += Math.round(100 / asteroid.radius) * 10;

        // Split asteroid
        if (asteroid.radius > 18) {
          const newRadius = asteroid.radius * 0.55;
          for (let i = 0; i < 2; i++) {
            newAsteroids.push(createAsteroid(asteroid.x, asteroid.y, newRadius));
          }
        }
        return false;
      }
      return true;
    });
    state.asteroids.push(...newAsteroids);

    // Ship-asteroid collision
    const shipHit = state.asteroids.some(
      (a) => Math.hypot(a.x - ship.x, a.y - ship.y) < a.radius + SHIP_SIZE * 0.6
    );
    if (shipHit) {
      state.phase = 'gameover';
      return;
    }

    // Next level
    if (state.asteroids.length === 0) {
      state.level++;
      spawnAsteroids(3 + state.level);
    }
  };

  const draw = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const state = stateRef.current;
    const { width, height, deviceRatio } = state;

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.setTransform(deviceRatio, 0, 0, deviceRatio, 0, 0);

    ctx.fillStyle = bgColor;
    ctx.fillRect(0, 0, width, height);

    // Draw stars with twinkling
    state.stars.forEach((star) => {
      const twinkle = Math.sin(state.time * star.twinkleSpeed + star.twinkleOffset) * 0.3 + 0.7;
      ctx.globalAlpha = star.brightness * twinkle * (star.layer === 0 ? 0.4 : star.layer === 1 ? 0.6 : 0.9);
      ctx.fillStyle = fgColor;
      ctx.beginPath();
      ctx.arc(star.x, star.y, star.size, 0, TWO_PI);
      ctx.fill();
    });
    ctx.globalAlpha = 1;

    ctx.strokeStyle = fgColor;
    ctx.fillStyle = fgColor;
    ctx.lineWidth = 1.5;

    // Draw ship
    const ship = state.ship;
    ctx.save();
    ctx.translate(ship.x, ship.y);
    ctx.rotate(ship.angle);
    ctx.beginPath();
    ctx.moveTo(SHIP_SIZE, 0);
    ctx.lineTo(-SHIP_SIZE * 0.7, -SHIP_SIZE * 0.6);
    ctx.lineTo(-SHIP_SIZE * 0.4, 0);
    ctx.lineTo(-SHIP_SIZE * 0.7, SHIP_SIZE * 0.6);
    ctx.closePath();
    ctx.stroke();

    // Thrust flame
    if (keysRef.current.up && state.phase === 'playing') {
      ctx.strokeStyle = softColor;
      ctx.beginPath();
      ctx.moveTo(-SHIP_SIZE * 0.5, -SHIP_SIZE * 0.25);
      ctx.lineTo(-SHIP_SIZE * 1.1, 0);
      ctx.lineTo(-SHIP_SIZE * 0.5, SHIP_SIZE * 0.25);
      ctx.stroke();
      ctx.strokeStyle = fgColor;
    }
    ctx.restore();

    // Draw asteroids
    state.asteroids.forEach((a) => {
      ctx.beginPath();
      a.vertices.forEach((v, i) => {
        const px = a.x + Math.cos(v.angle) * v.dist;
        const py = a.y + Math.sin(v.angle) * v.dist;
        if (i === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      });
      ctx.closePath();
      ctx.stroke();
    });

    // Draw bullets
    state.bullets.forEach((b) => {
      ctx.fillRect(b.x - 2, b.y - 2, 4, 4);
    });

    // Score
    ctx.font = '16px "Courier New", monospace';
    ctx.fillText(`ASTEROIDS  SCORE ${state.score}  LEVEL ${state.level}`, 8, 22);

    if (state.phase === 'intro') {
      ctx.fillStyle = softColor;
      ctx.font = '15px "Courier New", monospace';
      ctx.fillText('Arrows to move, SPACE to shoot. Press ENTER to start.', 8, height - 12);
    }

    if (state.phase === 'gameover') {
      ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
      ctx.fillRect(0, 0, width, height);
      ctx.fillStyle = fgColor;
      ctx.font = '24px "Courier New", monospace';
      ctx.fillText('GAME OVER', Math.max(8, width / 2 - 72), height / 2 - 10);
      ctx.font = '16px "Courier New", monospace';
      ctx.fillStyle = softColor;
      ctx.fillText(`Score: ${state.score}`, Math.max(8, width / 2 - 50), height / 2 + 18);
      ctx.fillText('Press ENTER to restart or ESC to quit', Math.max(8, width / 2 - 158), height / 2 + 44);
    }
  };

  const handleTouchDirection = (dir: string) => {
    if (dir === 'left') keysRef.current.left = true;
    else if (dir === 'right') keysRef.current.right = true;
    else if (dir === 'up') keysRef.current.up = true;

    const state = stateRef.current;
    if (state.phase === 'intro') {
      state.phase = 'playing';
      state.lastTime = 0;
    }
    if (state.phase === 'gameover') {
      resetRound();
      state.phase = 'playing';
    }

    setTimeout(() => {
      if (dir === 'left') keysRef.current.left = false;
      else if (dir === 'right') keysRef.current.right = false;
      else if (dir === 'up') keysRef.current.up = false;
    }, 100);
  };

  const handleTouchAction = () => {
    const state = stateRef.current;
    if (state.phase === 'intro') {
      state.phase = 'playing';
      state.lastTime = 0;
      return;
    }
    if (state.phase === 'gameover') {
      resetRound();
      state.phase = 'playing';
      return;
    }
    shoot();
  };

  useEffect(() => {
    setIsTouchDevice('ontouchstart' in window || navigator.maxTouchPoints > 0);
    resizeCanvas();

    const observer = new ResizeObserver(() => resizeCanvas());
    if (containerRef.current) observer.observe(containerRef.current);

    const tick = (ts: number) => {
      update(ts);
      draw();
      animationRef.current = requestAnimationFrame(tick);
    };
    animationRef.current = requestAnimationFrame(tick);

    const handleKeyDown = (e: KeyboardEvent) => {
      const state = stateRef.current;
      if (e.key === 'Escape') {
        e.preventDefault();
        onExit(String(state.score));
        return;
      }

      if (e.key === 'ArrowLeft') keysRef.current.left = true;
      if (e.key === 'ArrowRight') keysRef.current.right = true;
      if (e.key === 'ArrowUp') keysRef.current.up = true;
      if (e.key === ' ') {
        e.preventDefault();
        keysRef.current.space = true;
      }

      if (state.phase === 'intro' && e.key === 'Enter') {
        state.phase = 'playing';
        state.lastTime = 0;
      }
      if (state.phase === 'gameover' && e.key === 'Enter') {
        e.preventDefault();
        resetRound();
        state.phase = 'playing';
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') keysRef.current.left = false;
      if (e.key === 'ArrowRight') keysRef.current.right = false;
      if (e.key === 'ArrowUp') keysRef.current.up = false;
      if (e.key === ' ') keysRef.current.space = false;
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      observer.disconnect();
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="game-canvas-wrap" ref={containerRef}>
      {isTouchDevice && <div className="game-touch-instruction">D-pad to move, tap FIRE to shoot.</div>}
      <canvas ref={canvasRef} className="game-canvas" />
      {isTouchDevice && (
        <TouchControls
          type="dpad"
          onDirection={handleTouchDirection}
          onAction={handleTouchAction}
          onQuit={() => onExit(String(stateRef.current.score))}
          actionLabel="FIRE"
        />
      )}
    </div>
  );
}
